/**
 * Restaurant Fallback Menu Items
 * 
 * This file contains fallback menu items for well-known restaurants
 * when internet search doesn't return menu items.
 * 
 * NOTE: This is a scalable system. You can add more restaurants here.
 * For a production app, this would be stored in a database.
 */

import type { RestaurantMenuItem } from './restaurantMenus';

interface RestaurantFallback {
  namePatterns: string[]; // Patterns to match restaurant name
  items: string[]; // Common menu items
  cuisine?: string; // Cuisine type for categorization
}

/**
 * Fallback menu data for known restaurants
 * 
 * To add a new restaurant:
 * 1. Add a new entry to RESTAURANT_FALLBACKS
 * 2. Include name patterns (variations of the restaurant name)
 * 3. List common menu items
 */
const RESTAURANT_FALLBACKS: RestaurantFallback[] = [
  {
    namePatterns: ['pf chang', 'chang\'s', 'changs', 'pfchang'],
    cuisine: 'chinese',
    items: [
      'Orange Chicken', 'General Tso\'s Chicken', 'Kung Pao Chicken', 'Mongolian Beef',
      'Beef with Broccoli', 'Honey Shrimp', 'Shrimp with Lobster Sauce', 'Pad Thai',
      'Lo Mein', 'Chow Mein', 'Fried Rice', 'Spring Rolls', 'Dumplings', 'Wonton Soup',
      'Hot and Sour Soup', 'Egg Drop Soup', 'Sesame Chicken', 'Lemon Chicken',
      'Cashew Chicken', 'Sweet and Sour Chicken', 'Teriyaki Chicken', 'Chicken Lo Mein',
      'Beef Lo Mein', 'Shrimp Lo Mein', 'Vegetable Lo Mein'
    ],
  },
  {
    namePatterns: ['olive garden'],
    cuisine: 'italian',
    items: [
      'Chicken Alfredo', 'Fettuccine Alfredo', 'Spaghetti with Meatballs', 'Lasagna Classico',
      'Chicken Parmigiana', 'Shrimp Scampi', 'Tour of Italy', 'Chicken Marsala',
      'Herb-Grilled Salmon', 'Garden Fresh Salad', 'Zuppa Toscana', 'Minestrone Soup',
      'Breadsticks', 'Tiramisu', 'Chocolate Brownie Lasagna'
    ],
  },
  {
    namePatterns: ['red lobster'],
    cuisine: 'seafood',
    items: [
      'Ultimate Feast', 'Lobster Tail', 'Snow Crab Legs', 'Shrimp Scampi',
      'Grilled Salmon', 'Lobster Bisque', 'New England Clam Chowder', 'Cheddar Bay Biscuits',
      'Crab Linguini', 'Shrimp Linguini Alfredo', 'Lobster Pizza', 'Crab Cakes'
    ],
  },
  {
    namePatterns: ['chipotle'],
    cuisine: 'mexican',
    items: [
      'Burrito Bowl', 'Burrito', 'Tacos', 'Salad', 'Quesadilla',
      'Carnitas', 'Barbacoa', 'Chicken', 'Steak', 'Sofritas',
      'White Rice', 'Brown Rice', 'Black Beans', 'Pinto Beans',
      'Guacamole', 'Salsa', 'Sour Cream', 'Cheese'
    ],
  },
  {
    namePatterns: ['panda express'],
    cuisine: 'chinese',
    items: [
      'Orange Chicken', 'Beijing Beef', 'Honey Walnut Shrimp', 'Kung Pao Chicken',
      'SweetFire Chicken Breast', 'Grilled Teriyaki Chicken', 'Black Pepper Chicken',
      'Mushroom Chicken', 'Broccoli Beef', 'String Bean Chicken Breast', 'Fried Rice',
      'Chow Mein', 'Super Greens', 'Eggplant Tofu'
    ],
  },
  {
    namePatterns: ['cheesecake factory', 'the cheesecake factory'],
    cuisine: 'american',
    items: [
      'SkinnyLicious Grilled Chicken', 'Grilled Salmon with Vegetables', 'SkinnyLicious House Salad',
      'SkinnyLicious Grilled Turkey Burger', 'Grilled Chicken and Avocado Club',
      'SkinnyLicious Grilled Chicken Pasta', 'Grilled Steak with Vegetables',
      'Original Cheesecake', 'Strawberry Cheesecake', 'Oreo Cheesecake'
    ],
  },
  {
    namePatterns: ['applebee\'s', 'applebees'],
    cuisine: 'american',
    items: [
      'Grilled Chicken Breast', 'Grilled Salmon', 'Sirloin Steak', 'Ribeye Steak',
      'Oriental Chicken Salad', 'Grilled Chicken Caesar Salad', 'House Salad',
      'Chicken Tenders', 'Boneless Wings', 'Mozzarella Sticks', 'Onion Rings'
    ],
  },
  {
    namePatterns: ['outback', 'outback steakhouse'],
    cuisine: 'american',
    items: [
      'Bloomin\' Onion', 'Aussie Cheese Fries', 'Grilled Shrimp on the Barbie',
      'Victoria\'s Filet', 'Outback Special', 'Ribeye', 'New York Strip',
      'Grilled Chicken', 'Grilled Salmon', 'Alice Springs Chicken', 'Aussie Cobb Salad'
    ],
  },
  {
    namePatterns: ['taco bell'],
    cuisine: 'mexican',
    items: [
      'Crunchwrap Supreme', 'Chalupa Supreme', 'Nachos BellGrande', 'Quesadilla',
      'Crunchy Taco', 'Soft Taco', 'Bean Burrito', 'Cheesy Gordita Crunch',
      'Mexican Pizza', 'Cinnamon Twists', 'Chips and Nacho Cheese'
    ],
  },
  {
    namePatterns: ['subway'],
    cuisine: 'american',
    items: [
      'Turkey Breast', 'Ham', 'Roast Beef', 'Chicken Breast', 'Tuna',
      'Veggie Delite', 'Italian B.M.T.', 'Meatball Marinara', 'Steak and Cheese',
      'Subway Club', 'Oven Roasted Chicken', 'Rotisserie-Style Chicken'
    ],
  },
  {
    namePatterns: ['mcdonald\'s', 'mcdonalds'],
    cuisine: 'american',
    items: [
      'Big Mac', 'Quarter Pounder', 'McChicken', 'Filet-O-Fish', 'Chicken McNuggets',
      'McDouble', 'Hamburger', 'Cheeseburger', 'Fries', 'Apple Slices',
      'Grilled Chicken Sandwich', 'Grilled Chicken Salad'
    ],
  },
  {
    namePatterns: ['burger king'],
    cuisine: 'american',
    items: [
      'Whopper', 'Whopper Jr.', 'Double Whopper', 'Chicken Sandwich', 'Chicken Fries',
      'Original Chicken Sandwich', 'Bacon King', 'Impossible Whopper', 'Onion Rings',
      'French Fries', 'Garden Side Salad'
    ],
  },
  {
    namePatterns: ['wendy\'s', 'wendys'],
    cuisine: 'american',
    items: [
      'Dave\'s Single', 'Dave\'s Double', 'Baconator', 'Spicy Chicken Sandwich',
      'Grilled Chicken Sandwich', 'Caesar Side Salad', 'Apple Pecan Chicken Salad',
      'Chili', 'Baked Potato', 'Fries', 'Nuggets'
    ],
  },
  {
    namePatterns: ['kfc'],
    cuisine: 'american',
    items: [
      'Original Recipe Chicken', 'Extra Crispy Chicken', 'Hot Wings', 'Chicken Tenders',
      'Popcorn Chicken', 'Chicken Sandwich', 'Grilled Chicken', 'Colonel\'s Crispy Sandwich',
      'Mac and Cheese', 'Mashed Potatoes', 'Coleslaw', 'Green Beans'
    ],
  },
  {
    namePatterns: ['dunkin\' donuts', 'dunkin donuts', 'dunkin'],
    cuisine: 'american',
    items: [
      'Egg White Veggie Wake-Up Wrap', 'Turkey Sausage Wake-Up Wrap',
      'Ham, Egg & Cheese Wake-Up Wrap', 'Bacon, Egg & Cheese Wake-Up Wrap',
      'Oatmeal', 'Hash Browns', 'Bagel', 'Muffin'
    ],
  },
];

/**
 * Get fallback menu items for a restaurant
 * 
 * This function matches the restaurant name against known patterns
 * and returns common menu items for that restaurant.
 */
export function getFallbackMenuItems(restaurantName: string): RestaurantMenuItem[] {
  const lowerName = restaurantName.toLowerCase().replace(/['"]/g, '').trim();
  const items: RestaurantMenuItem[] = [];
  
  // Find matching restaurant fallback
  for (const fallback of RESTAURANT_FALLBACKS) {
    const matches = fallback.namePatterns.some(pattern => 
      lowerName.includes(pattern.toLowerCase()) || 
      pattern.toLowerCase().includes(lowerName)
    );
    
    if (matches) {
      console.log(`[Fallback] Found match for ${restaurantName} - ${fallback.namePatterns[0]}`);
      
      fallback.items.forEach(itemName => {
        items.push({
          name: itemName,
          explanation: `Menu item from ${restaurantName} (common dish)`,
          acneImpactScore: 50, // Will be analyzed later
          restaurantName: restaurantName,
          cuisine: fallback.cuisine,
        });
      });
      
      break; // Only use first match
    }
  }
  
  return items;
}

/**
 * Get all supported restaurants (for UI display)
 */
export function getSupportedRestaurants(): string[] {
  return RESTAURANT_FALLBACKS.flatMap(fallback => fallback.namePatterns);
}



